'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { Moon, Sun, Clock, Sparkles, Info, Calendar, ChevronRight, RefreshCw, Star } from 'lucide-react';
import AdUnit from '@/components/AdUnit';

// Metadata configuration for SEO is done at the layout level or page wrapper in Next.js.
// Since this is a client component, we will set up head metadata inside the layout or render a custom head element if needed.
// In Next.js, we can also use dynamic metadata. Let's make sure it has standard metadata.

interface AgeGroup {
  name: string;
  recommendedHours: string;
  minCycles: number;
  maxCycles: number;
  description: string;
}

const AGE_GROUPS: Record<string, AgeGroup> = {
  toddler: {
    name: 'Toddler (1-2 years)',
    recommendedHours: '11 - 14 hours',
    minCycles: 7,
    maxCycles: 9,
    description: 'Toddlers require significant sleep for rapid mental and physical development.'
  },
  preschool: {
    name: 'Preschooler (3-5 years)',
    recommendedHours: '10 - 13 hours',
    minCycles: 7,
    maxCycles: 8,
    description: 'Preschoolers transition away from daily naps and need solid nighttime sleep.'
  },
  schoolage: {
    name: 'School Age (6-12 years)',
    recommendedHours: '9 - 12 hours',
    minCycles: 6,
    maxCycles: 8,
    description: 'School-age children need quality sleep for focus, memory, and growth.'
  },
  teenager: {
    name: 'Teenager (13-18 years)',
    recommendedHours: '8 - 10 hours',
    minCycles: 5,
    maxCycles: 7,
    description: 'Circadian rhythms shift in teens, making them naturally sleepier later.'
  },
  adult: {
    name: 'Adult (18-64 years)',
    recommendedHours: '7 - 9 hours',
    minCycles: 5,
    maxCycles: 6,
    description: 'Most adults require 5 to 6 complete sleep cycles to function optimally.'
  },
  senior: {
    name: 'Senior (65+ years)',
    recommendedHours: '7 - 8 hours',
    minCycles: 5,
    maxCycles: 5,
    description: 'Seniors may experience lighter sleep, but still need around 7 to 8 hours.'
  }
};

export default function SleepCalculator() {
  const [activeTab, setActiveTab] = useState<'wakeup' | 'bedtime'>('wakeup');
  const [wakeHour, setWakeHour] = useState('07');
  const [wakeMinute, setWakeMinute] = useState('00');
  const [wakePeriod, setWakePeriod] = useState('AM');
  
  const [customBedHour, setCustomBedHour] = useState('11');
  const [customBedMinute, setCustomBedMinute] = useState('00');
  const [customBedPeriod, setCustomBedPeriod] = useState('PM');
  const [bedtimeMode, setBedtimeMode] = useState<'now' | 'custom'>('now');

  const [ageGroup, setAgeGroup] = useState('adult');
  const [latency, setLatency] = useState(15); // minutes to fall asleep
  const [results, setResults] = useState<{ time: string; duration: string; cycles: number; isOptimal: boolean }[]>([]);

  // Clock state for current time display
  const [currentTime, setCurrentTime] = useState<Date | null>(null);

  useEffect(() => {
    setCurrentTime(new Date());
    const timer = setInterval(() => setCurrentTime(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);

  // Run calculation when parameters change
  useEffect(() => {
    calculateSleepTimes();
  }, [activeTab, wakeHour, wakeMinute, wakePeriod, customBedHour, customBedMinute, customBedPeriod, bedtimeMode, ageGroup, latency, currentTime]);

  const calculateSleepTimes = () => {
    const cycleLength = 90; // minutes per sleep cycle
    const group = AGE_GROUPS[ageGroup];
    const newResults: { time: string; duration: string; cycles: number; isOptimal: boolean }[] = [];

    if (activeTab === 'wakeup') {
      // Calculate bedside times backward from the desired wake-up time
      let hours = parseInt(wakeHour);
      if (wakePeriod === 'PM' && hours !== 12) hours += 12;
      if (wakePeriod === 'AM' && hours === 12) hours = 0;

      const targetDate = new Date();
      targetDate.setHours(hours, parseInt(wakeMinute), 0, 0);

      // We calculate for 1 to 8 cycles
      for (let i = 1; i <= 8; i++) {
        const totalMinutes = i * cycleLength + latency;
        const resultDate = new Date(targetDate.getTime() - totalMinutes * 60 * 1000);
        
        const formattedTime = resultDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        const durationHours = Math.floor((i * cycleLength) / 60);
        const durationMins = (i * cycleLength) % 60;
        const durationStr = `${durationHours}h ${durationMins > 0 ? durationMins + 'm' : ''}`;
        
        const isOptimal = i >= group.minCycles && i <= group.maxCycles;

        newResults.push({
          time: formattedTime,
          duration: durationStr,
          cycles: i,
          isOptimal
        });
      }
      // Sort so the ones with standard sleep duration (e.g. 5-6 cycles) are displayed logically
      // For bedside calculations, waking up is the target, so earlier bedtimes (more cycles) are listed first
      setResults(newResults.reverse());
    } else {
      // Calculate wake-up times forward from bedtime
      let baseDate = new Date();
      if (bedtimeMode === 'custom') {
        let hours = parseInt(customBedHour);
        if (customBedPeriod === 'PM' && hours !== 12) hours += 12;
        if (customBedPeriod === 'AM' && hours === 12) hours = 0;
        baseDate.setHours(hours, parseInt(customBedMinute), 0, 0);
      }

      // We calculate for 1 to 8 cycles
      for (let i = 1; i <= 8; i++) {
        const totalMinutes = i * cycleLength + latency;
        const resultDate = new Date(baseDate.getTime() + totalMinutes * 60 * 1000);
        
        const formattedTime = resultDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        const durationHours = Math.floor((i * cycleLength) / 60);
        const durationMins = (i * cycleLength) % 60;
        const durationStr = `${durationHours}h ${durationMins > 0 ? durationMins + 'm' : ''}`;
        
        const isOptimal = i >= group.minCycles && i <= group.maxCycles;

        newResults.push({
          time: formattedTime,
          duration: durationStr,
          cycles: i,
          isOptimal
        });
      }
      setResults(newResults);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 font-sans pb-16">
      {/* Background visual element */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(99,102,241,0.15),transparent_50%)] pointer-events-none z-0" />
      
      <div className="max-w-4xl mx-auto px-4 py-8 relative z-10">
        
        {/* AdSense Unit - Top */}
        <AdUnit />

        {/* Hero Section */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-indigo-500/10 border border-indigo-500/20 text-indigo-400 text-xs font-semibold mb-3 animate-pulse">
            <Moon className="w-3.5 h-3.5 fill-indigo-400/20" />
            Sleep Cycle Calculator
          </div>
          <h1 className="text-3xl md:text-5xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-indigo-200 via-purple-200 to-pink-200 tracking-tight mb-3">
            Wake Up Refreshed & Reenergized
          </h1>
          <p className="text-slate-400 text-sm md:text-base max-w-lg mx-auto leading-relaxed">
            Optimize your sleep by aligning your bedtime with natural 90-minute sleep cycles. Minimize grogginess and sleep smarter.
          </p>
        </div>

        {/* Calculator Card */}
        <div className="bg-slate-900/60 backdrop-blur-xl border border-slate-800 rounded-3xl p-6 md:p-8 shadow-2xl shadow-slate-950/50 mb-10">
          
          {/* Tabs */}
          <div className="grid grid-cols-2 p-1.5 bg-slate-950/80 border border-slate-800/60 rounded-2xl mb-8">
            <button
              onClick={() => setActiveTab('wakeup')}
              className={`py-3 text-xs md:text-sm font-semibold rounded-xl flex items-center justify-center gap-2.5 transition-all duration-300 ${
                activeTab === 'wakeup'
                  ? 'bg-gradient-to-r from-indigo-600 to-indigo-700 text-white shadow-lg shadow-indigo-900/30'
                  : 'text-slate-400 hover:text-slate-200 hover:bg-slate-900/40'
              }`}
            >
              <Sun className="w-4 h-4" />
              Calculate Bedtime
            </button>
            <button
              onClick={() => setActiveTab('bedtime')}
              className={`py-3 text-xs md:text-sm font-semibold rounded-xl flex items-center justify-center gap-2.5 transition-all duration-300 ${
                activeTab === 'bedtime'
                  ? 'bg-gradient-to-r from-indigo-600 to-indigo-700 text-white shadow-lg shadow-indigo-900/30'
                  : 'text-slate-400 hover:text-slate-200 hover:bg-slate-900/40'
              }`}
            >
              <Moon className="w-4 h-4" />
              Calculate Wake Time
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-start">
            
            {/* Inputs Column */}
            <div className="md:col-span-1 space-y-5 bg-slate-950/40 border border-slate-800/50 p-5 rounded-2xl">
              <h3 className="text-xs font-bold text-slate-400 uppercase tracking-widest flex items-center gap-2">
                <Clock className="w-3.5 h-3.5 text-indigo-400" />
                Calculator Options
              </h3>

              {/* Time Picker */}
              {activeTab === 'wakeup' ? (
                <div>
                  <label className="block text-xs font-semibold text-slate-300 mb-2">
                    I want to wake up at:
                  </label>
                  <div className="flex gap-2">
                    <select
                      value={wakeHour}
                      onChange={(e) => setWakeHour(e.target.value)}
                      className="flex-1 bg-slate-900 border border-slate-800 rounded-xl px-3 py-2.5 text-sm font-semibold text-slate-100 focus:outline-none focus:border-indigo-500 transition"
                    >
                      {Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, '0')).map(h => (
                        <option key={h} value={h}>{h}</option>
                      ))}
                    </select>
                    <select
                      value={wakeMinute}
                      onChange={(e) => setWakeMinute(e.target.value)}
                      className="flex-1 bg-slate-900 border border-slate-800 rounded-xl px-3 py-2.5 text-sm font-semibold text-slate-100 focus:outline-none focus:border-indigo-500 transition"
                    >
                      {['00', '05', '10', '15', '20', '25', '30', '35', '40', '45', '50', '55'].map(m => (
                        <option key={m} value={m}>{m}</option>
                      ))}
                    </select>
                    <select
                      value={wakePeriod}
                      onChange={(e) => setWakePeriod(e.target.value)}
                      className="bg-slate-900 border border-slate-800 rounded-xl px-3 py-2.5 text-sm font-semibold text-slate-100 focus:outline-none focus:border-indigo-500 transition"
                    >
                      <option value="AM">AM</option>
                      <option value="PM">PM</option>
                    </select>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <label className="block text-xs font-semibold text-slate-300">
                    I will go to bed:
                  </label>
                  <div className="grid grid-cols-2 gap-2 p-1 bg-slate-900 border border-slate-800 rounded-xl">
                    <button
                      onClick={() => setBedtimeMode('now')}
                      className={`py-1.5 text-xs font-semibold rounded-lg transition-all ${
                        bedtimeMode === 'now' ? 'bg-indigo-600 text-white' : 'text-slate-400 hover:text-slate-200'
                      }`}
                    >
                      Right Now
                    </button>
                    <button
                      onClick={() => setBedtimeMode('custom')}
                      className={`py-1.5 text-xs font-semibold rounded-lg transition-all ${
                        bedtimeMode === 'custom' ? 'bg-indigo-600 text-white' : 'text-slate-400 hover:text-slate-200'
                      }`}
                    >
                      Specific Time
                    </button>
                  </div>

                  {bedtimeMode === 'custom' && (
                    <div className="flex gap-2 animate-in fade-in slide-in-from-top-1 duration-200">
                      <select
                        value={customBedHour}
                        onChange={(e) => setCustomBedHour(e.target.value)}
                        className="flex-1 bg-slate-900 border border-slate-800 rounded-xl px-3 py-2.5 text-sm font-semibold text-slate-100 focus:outline-none focus:border-indigo-500 transition"
                      >
                        {Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, '0')).map(h => (
                          <option key={h} value={h}>{h}</option>
                        ))}
                      </select>
                      <select
                        value={customBedMinute}
                        onChange={(e) => setCustomBedMinute(e.target.value)}
                        className="flex-1 bg-slate-900 border border-slate-800 rounded-xl px-3 py-2.5 text-sm font-semibold text-slate-100 focus:outline-none focus:border-indigo-500 transition"
                      >
                        {['00', '05', '10', '15', '20', '25', '30', '35', '40', '45', '50', '55'].map(m => (
                          <option key={m} value={m}>{m}</option>
                        ))}
                      </select>
                      <select
                        value={customBedPeriod}
                        onChange={(e) => setCustomBedPeriod(e.target.value)}
                        className="bg-slate-900 border border-slate-800 rounded-xl px-3 py-2.5 text-sm font-semibold text-slate-100 focus:outline-none focus:border-indigo-500 transition"
                      >
                        <option value="AM">AM</option>
                        <option value="PM">PM</option>
                      </select>
                    </div>
                  )}

                  {bedtimeMode === 'now' && currentTime && (
                    <div className="text-center py-2.5 bg-slate-900/60 border border-slate-800/80 rounded-xl">
                      <span className="text-[10px] text-slate-400 block mb-0.5">Current Time:</span>
                      <span className="text-sm font-bold text-slate-200">
                        {currentTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                      </span>
                    </div>
                  )}
                </div>
              )}

              {/* Age Selector */}
              <div>
                <label className="block text-xs font-semibold text-slate-300 mb-2">
                  Sleep Category / Age:
                </label>
                <select
                  value={ageGroup}
                  onChange={(e) => setAgeGroup(e.target.value)}
                  className="w-full bg-slate-900 border border-slate-800 rounded-xl px-3 py-2.5 text-sm font-semibold text-slate-100 focus:outline-none focus:border-indigo-500 transition"
                >
                  {Object.entries(AGE_GROUPS).map(([key, value]) => (
                    <option key={key} value={key}>{value.name}</option>
                  ))}
                </select>
              </div>

              {/* Latency Picker */}
              <div>
                <div className="flex justify-between text-xs font-semibold mb-2">
                  <span className="text-slate-300">Time to fall asleep:</span>
                  <span className="text-indigo-400">{latency} mins</span>
                </div>
                <select
                  value={latency}
                  onChange={(e) => setLatency(parseInt(e.target.value))}
                  className="w-full bg-slate-900 border border-slate-800 rounded-xl px-3 py-2.5 text-sm font-semibold text-slate-100 focus:outline-none focus:border-indigo-500 transition"
                >
                  <option value="0">0 minutes (Instant)</option>
                  <option value="5">5 minutes</option>
                  <option value="10">10 minutes</option>
                  <option value="15">15 minutes (Average)</option>
                  <option value="20">20 minutes</option>
                  <option value="25">25 minutes</option>
                  <option value="30">30 minutes</option>
                </select>
              </div>
            </div>

            {/* Results Column */}
            <div className="md:col-span-2 space-y-4">
              <div className="flex justify-between items-center px-1">
                <h3 className="text-sm font-bold text-slate-300 flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-amber-400" />
                  {activeTab === 'wakeup' ? 'Recommended Bedtimes' : 'Recommended Wake-up Times'}
                </h3>
                <div className="text-[11px] text-slate-400 flex items-center gap-1.5">
                  <Info className="w-3.5 h-3.5" />
                  Includes {latency}m buffer
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {results.map((res, index) => (
                  <div
                    key={index}
                    className={`border rounded-2xl p-4 transition-all duration-300 relative overflow-hidden flex flex-col justify-between ${
                      res.isOptimal
                        ? 'bg-gradient-to-br from-indigo-950/80 via-slate-900 to-slate-900 border-indigo-500/50 shadow-md shadow-indigo-950/30 hover:border-indigo-400 scale-[1.01]'
                        : 'bg-slate-900/30 border-slate-800/80 hover:border-slate-700 hover:bg-slate-900/50'
                    }`}
                  >
                    {res.isOptimal && (
                      <span className="absolute top-2.5 right-3 bg-indigo-500/25 text-indigo-300 border border-indigo-500/30 px-2 py-0.5 rounded-full text-[9px] font-extrabold tracking-wider uppercase flex items-center gap-1">
                        <Star className="w-2.5 h-2.5 fill-indigo-300 text-indigo-300" />
                        Optimal
                      </span>
                    )}

                    <div>
                      <div className="text-[10px] text-slate-400 font-bold uppercase tracking-wider mb-1">
                        {res.cycles} Sleep {res.cycles === 1 ? 'Cycle' : 'Cycles'}
                      </div>
                      <div className="text-2xl font-black text-white tracking-tight">{res.time}</div>
                    </div>

                    <div className="mt-4 flex items-center justify-between text-xs pt-2.5 border-t border-slate-800/60">
                      <span className="text-slate-400">Sleep time:</span>
                      <span className={`font-semibold ${res.isOptimal ? 'text-indigo-300' : 'text-slate-300'}`}>
                        {res.duration}
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              <div className="bg-slate-950/40 border border-slate-800/60 rounded-xl p-4 text-[11px] text-slate-400 leading-relaxed flex items-start gap-2.5">
                <Info className="w-4 h-4 text-indigo-400 shrink-0 mt-0.5" />
                <div>
                  <strong className="text-slate-300 block mb-0.5">Understanding the calculations:</strong>
                  Waking up at the end of a sleep cycle ensures you feel fresh. Highlighting "Optimal" times helps you get the target <span className="text-indigo-400 font-bold">{AGE_GROUPS[ageGroup].recommendedHours}</span> of sleep recommended for the <span className="text-indigo-400 font-bold">{AGE_GROUPS[ageGroup].name.split(' (')[0]}</span> age group.
                </div>
              </div>
            </div>

          </div>
        </div>

        {/* AdSense Unit - Bottom */}
        <AdUnit />

        {/* Educational Content / Guide */}
        <div className="space-y-8 bg-slate-900/25 border border-slate-900 rounded-3xl p-6 md:p-10">
          
          <h2 className="text-2xl font-bold tracking-tight text-white flex items-center gap-2">
            <span className="w-1.5 h-6 bg-indigo-500 rounded-full" />
            Complete Guide to Sleep Cycles & Circadian Rhythms
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-slate-300 text-sm leading-relaxed">
            
            <div className="space-y-4">
              <h3 className="text-base font-bold text-white">What is a Sleep Cycle?</h3>
              <p>
                A standard sleep cycle lasts about **90 minutes** and is made up of four distinct stages: three NREM (Non-Rapid Eye Movement) stages and one REM (Rapid Eye Movement) stage.
              </p>
              <p>
                *   **Stage 1 & 2 (Light Sleep):** The transition from wakefulness to sleep. Heart rate slows and body temperature drops.
                *   **Stage 3 (Deep Sleep):** Critical for cellular repair, growth hormone release, and body recovery. Waking up during this stage makes you feel intensely groggy and tired (sleep inertia).
                *   **Stage 4 (REM Sleep):** High brain activity, dreaming, and memory consolidation. Waking up at the end of REM is the most natural transition to wakefulness.
              </p>
            </div>

            <div className="space-y-4">
              <h3 className="text-base font-bold text-white">Why Waking Up Between Cycles Matters</h3>
              <p>
                Even if you sleep for 8 hours, waking up in the middle of Stage 3 (Deep Sleep) can trigger **sleep inertia**, leaving you feeling exhausted and disoriented for hours.
              </p>
              <p>
                By calculating bedtime based on 90-minute blocks (e.g., 4.5, 6.0, 7.5, or 9.0 hours) and adding a small buffer (usually **15 minutes**) to fall asleep, you can program your alarms to go off at the end of a cycle, helping you wake up feeling energetic and clear-headed.
              </p>
            </div>
            
          </div>

          <hr className="border-slate-800" />

          {/* Age Recommendations Table */}
          <div className="overflow-x-auto rounded-xl border border-slate-800 bg-slate-950/60 p-4">
            <h3 className="text-sm font-bold text-slate-200 mb-3">National Sleep Foundation Guidelines</h3>
            <table className="w-full text-xs text-left text-slate-400">
              <thead className="text-[10px] text-slate-300 uppercase bg-slate-900/80">
                <tr>
                  <th scope="col" className="px-4 py-2 rounded-l-lg">Age Group</th>
                  <th scope="col" className="px-4 py-2">Sleep Target</th>
                  <th scope="col" className="px-4 py-2">Typical Cycles</th>
                  <th scope="col" className="px-4 py-2 rounded-r-lg">Importance</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(AGE_GROUPS).map(([key, group]) => (
                  <tr key={key} className="border-b border-slate-900 hover:bg-slate-900/30">
                    <td className="px-4 py-3 font-semibold text-slate-200">{group.name}</td>
                    <td className="px-4 py-3 text-indigo-400 font-bold">{group.recommendedHours}</td>
                    <td className="px-4 py-3">{group.minCycles} - {group.maxCycles} cycles</td>
                    <td className="px-4 py-3">{group.description}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Sleep Hygiene Tips */}
          <div className="space-y-4">
            <h3 className="text-base font-bold text-white flex items-center gap-2">
              <Sparkles className="w-4.5 h-4.5 text-amber-400" />
              Tips for Better Sleep Quality
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="bg-slate-950/60 border border-slate-800/80 p-4 rounded-xl">
                <span className="text-indigo-400 font-bold block mb-1">Consistency</span>
                Keep a stable sleep and wake schedule, even on weekends, to stabilize your circadian rhythm.
              </div>
              <div className="bg-slate-950/60 border border-slate-800/80 p-4 rounded-xl">
                <span className="text-indigo-400 font-bold block mb-1">Limit Blue Light</span>
                Avoid phone, computer, and TV screens for at least 30–60 minutes before bedtime.
              </div>
              <div className="bg-slate-950/60 border border-slate-800/80 p-4 rounded-xl">
                <span className="text-indigo-400 font-bold block mb-1">Environment</span>
                Keep your bedroom quiet, completely dark, and cool (ideally between 60°F and 67°F).
              </div>
            </div>
          </div>

        </div>

      </div>
    </div>
  );
}
